/**
 * CS-3b — the shared Spec Book, read by anybody with the link.
 *
 * No login, no session: the token in the path is the whole credential, as the
 * shared Spec Sheet works. The rows are a whitelist built on the server from
 * the saved columns, and they are read fresh on every open — a link sent in
 * March shows June's list in June.
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import ScheduleBook from '@/components/ScheduleBook';
import '@/styles/schedules.css';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

export default function PublicSchedulePage() {
  const { token } = useParams();
  const [book, setBook] = useState(null);
  const [gone, setGone] = useState(false);
  const [missing, setMissing] = useState(false);

  // CS-3c — the client's answer goes straight down the token, and the cell
  // shows what came back.
  const answer = async (clipId, status, notes) => {
    try {
      const { data } = await axios.post(
        `${API}/public/schedule/${token}/rows/${clipId}/answer`, { status, notes: notes || '' });
      return data;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    axios.get(`${API}/public/schedule/${token}`)
      .then(({ data }) => setBook(data))
      .catch((error) => {
        if (error.response?.status === 410) setGone(true); else setMissing(true);
      });
  }, [token]);

  useEffect(() => {
    if (!book) return;
    document.title = [book.firm?.name, book.schedule?.name].filter(Boolean).join(' · ');
  }, [book]);

  // The Spec Sheet's public print: paper colours whichever skin is on screen.
  useEffect(() => {
    const before = () => {
      document.body.dataset.kmSkinBeforePrint = document.body.dataset.kmSkin || '';
      document.body.dataset.kmSkin = 'studio';
    };
    const after = () => {
      const previous = document.body.dataset.kmSkinBeforePrint;
      if (previous) document.body.dataset.kmSkin = previous; else delete document.body.dataset.kmSkin;
      delete document.body.dataset.kmSkinBeforePrint;
    };
    window.addEventListener('beforeprint', before);
    window.addEventListener('afterprint', after);
    return () => {
      window.removeEventListener('beforeprint', before);
      window.removeEventListener('afterprint', after);
    };
  }, []);

  // PR-1 — the shared Spec Book is the document on this page.
  useEffect(() => {
    document.body.dataset.kmPrint = 'spec-book';
    return () => { delete document.body.dataset.kmPrint; };
  }, []);

  if (gone) {
    return (
      <div className="ksh" style={{ display: 'block' }} data-testid="public-book-gone">
        <div className="ksh__prev"><div className="ksh-pg">This Spec Book is no longer shared.</div></div>
      </div>
    );
  }
  if (missing) {
    return (
      <div className="ksh" style={{ display: 'block' }} data-testid="public-book-missing">
        <div className="ksh__prev"><div className="ksh-pg">No Spec Book at this link.</div></div>
      </div>
    );
  }
  if (!book) {
    return (
      <div className="ksh" style={{ display: 'block' }} data-testid="public-book-loading">
        <div className="ksh__prev"><div className="ksh-pg">Loading…</div></div>
      </div>
    );
  }

  return (
    <div className="ksh km-doc km-doc--landscape" style={{ display: 'block' }} data-testid="public-book">
      <div className="ksh__prev overflow-x-auto">
        <ScheduleBook
          firm={{ name: book.firm?.name,
            mark: book.firm?.mark
              ? (book.firm.mark.startsWith('/') ? `${BACKEND}${book.firm.mark}` : book.firm.mark)
              : null }}
          project={book.project}
          schedule={book.schedule}
          rooms={book.rooms}
          rows={book.rows}
          imageBase={BACKEND}
          onAnswer={book.schedule?.allow_answers === false ? null : answer} />
      </div>
    </div>
  );
}
